/**
 * PlanDemo
 *
 * A scripted replay of the Plan flow — Describe, Ground, Review — for
 * anonymous visitors on the landing page. Every line is static content from
 * `content/landing`: no API calls, no AI, nothing to sign in for. The steps
 * autoplay until the visitor picks a step or tabs into the deck — moving a
 * focused panel out from under a keyboard reader would drop focus to the
 * document body — and each step reveals its lines in
 * sequence so the grounding reads as work happening rather than a wall of
 * text. Both behaviours stop under `prefers-reduced-motion`.
 */

import {
  Component,
  For,
  Show,
  createEffect,
  createSignal,
  onCleanup,
} from "solid-js";
import { DEMO, type DemoStory } from "../../content/landing";

export const DEMO_STEP_MS = 4000;
const REVEAL_MS = 350;

/** Copied verbatim from `pages/PlanPage.tsx` — importing it pulls the app. */
const STEPS = [
  {
    title: "Describe",
    description:
      "Say what you want to build in plain language — no ticket format required.",
  },
  {
    title: "Ground",
    description:
      "Flowicity reads any repo you've linked, your documents, and your backlog to anchor the breakdown in what already exists.",
  },
  {
    title: "Review",
    description:
      "Adjust the proposed items, then send the ones you keep straight to the backlog.",
  },
];

/** Mirrors `components/plan/EstimateChip.tsx`, which pulls `ui/Tooltip`. */
const CONFIDENCE_DOT_CLASSES: Record<DemoStory["confidence"], string> = {
  high: "bg-signal-green",
  medium: "bg-signal-amber",
  low: "bg-ink-3",
};

const CONFIDENCE_LABELS: Record<DemoStory["confidence"], string> = {
  high: "high confidence",
  medium: "medium confidence",
  low: "low confidence",
};

/** Lines each step stages in; step 1's heading content is never staged. */
const STAGED_COUNTS = [0, DEMO.grounding.length, DEMO.stories.length];

const panelClass = "flex flex-col gap-3 sm:min-h-[20rem]";
const cardClass = "rounded border border-line bg-surface p-4";

const tabClass = (selected: boolean) =>
  "rounded border px-3 py-1.5 text-sm font-medium focus-ring " +
  (selected
    ? "border-line-strong bg-surface text-ink"
    : "border-line bg-paper text-ink-2 hover:text-ink");

export const PlanDemo: Component = () => {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [step, setStep] = createSignal(0);
  const [revealed, setRevealed] = createSignal(1);
  const revealedCount = () => (reduced ? Infinity : revealed());

  let autoTimer: ReturnType<typeof setInterval> | undefined;
  const stopAutoplay = () => {
    if (autoTimer !== undefined) {
      clearInterval(autoTimer);
      autoTimer = undefined;
    }
  };

  if (!reduced) {
    autoTimer = setInterval(
      () => setStep((current) => (current + 1) % STEPS.length),
      DEMO_STEP_MS,
    );
  }
  onCleanup(stopAutoplay);

  createEffect(() => {
    const total = STAGED_COUNTS[step()];
    setRevealed(1);
    if (reduced || total <= 1) return;
    const revealTimer = setInterval(() => {
      setRevealed((shown) => {
        if (shown + 1 >= total) clearInterval(revealTimer);
        return shown + 1;
      });
    }, REVEAL_MS);
    onCleanup(() => clearInterval(revealTimer));
  });

  const selectStep = (index: number) => {
    stopAutoplay();
    setStep(index);
  };

  return (
    <div class="flex flex-col gap-4" onFocusIn={stopAutoplay}>
      <div
        role="tablist"
        aria-label="Plan walkthrough steps"
        class="flex flex-wrap gap-2"
      >
        <For each={STEPS}>
          {(stepDef, index) => (
            <button
              type="button"
              role="tab"
              id={`plan-demo-tab-${index()}`}
              aria-selected={step() === index()}
              aria-controls={`plan-demo-panel-${index()}`}
              onClick={() => selectStep(index())}
              class={tabClass(step() === index())}
            >
              {stepDef.title}
            </button>
          )}
        </For>
      </div>

      <Show when={step() === 0}>
        <div
          role="tabpanel"
          id="plan-demo-panel-0"
          aria-labelledby="plan-demo-tab-0"
          tabindex="0"
          class={panelClass}
        >
          <p class="text-sm text-ink-2">{STEPS[0].description}</p>
          <div class="rounded border border-line bg-sunken p-4">
            <p class="font-mono text-sm text-ink">{DEMO.initiative}</p>
          </div>
        </div>
      </Show>

      <Show when={step() === 1}>
        <div
          role="tabpanel"
          id="plan-demo-panel-1"
          aria-labelledby="plan-demo-tab-1"
          tabindex="0"
          class={panelClass}
        >
          <p class="text-sm text-ink-2">{STEPS[1].description}</p>
          <ul class={cardClass + " gap-2 flex flex-col"}>
            <For each={DEMO.grounding}>
              {(line, index) => (
                <Show when={index() < revealedCount()}>
                  <li class="flex items-start gap-2 text-sm text-ink-2">
                    <span
                      class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-signal-green"
                      aria-hidden="true"
                    />
                    <span>{line}</span>
                  </li>
                </Show>
              )}
            </For>
          </ul>
        </div>
      </Show>

      <Show when={step() === 2}>
        <div
          role="tabpanel"
          id="plan-demo-panel-2"
          aria-labelledby="plan-demo-tab-2"
          tabindex="0"
          class={panelClass}
        >
          <p class="text-sm text-ink-2">{STEPS[2].description}</p>
          <div class={cardClass}>
            <p class="font-mono text-xs uppercase tracking-wide text-ink-3">
              Epic
            </p>
            <h3 class="text-sm font-medium text-ink">{DEMO.epic.title}</h3>
            <p class="text-sm text-ink-2">{DEMO.epic.summary}</p>
          </div>
          <ul class="flex flex-col gap-2">
            <For each={DEMO.stories}>
              {(story, index) => (
                <Show when={index() < revealedCount()}>
                  <li class={cardClass + " flex flex-col gap-2"}>
                    <div class="flex items-start justify-between gap-3">
                      <h4 class="text-sm font-medium text-ink">
                        {story.title}
                      </h4>
                      <span class="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-line bg-sunken px-2.5 py-1 font-mono text-xs font-medium text-ink-2">
                        <span
                          class={
                            "h-1.5 w-1.5 rounded-full " +
                            CONFIDENCE_DOT_CLASSES[story.confidence]
                          }
                          aria-hidden="true"
                        />
                        {story.estimate}
                        <span class="sr-only">
                          , {CONFIDENCE_LABELS[story.confidence]}
                        </span>
                      </span>
                    </div>
                    <p class="text-sm text-ink-2">{story.description}</p>
                    <ul class="flex flex-wrap gap-1.5">
                      <For each={story.areas}>
                        {(area) => (
                          <li class="rounded bg-sunken px-2 py-0.5 font-mono text-xs text-ink-3">
                            {area}
                          </li>
                        )}
                      </For>
                    </ul>
                  </li>
                </Show>
              )}
            </For>
          </ul>
          <p class="text-sm text-ink-2">
            Approve → 1 epic and 4 stories land in your backlog
          </p>
        </div>
      </Show>

      <p class="text-xs text-ink-3">Example walkthrough</p>
    </div>
  );
};
